/**
 * Loading component models.
 *
 * The models are authored Y-up with their footprint in XZ, and the game is
 * Z-up, so every one needs the same quarter turn on import. That is now the
 * only correction made.
 *
 * They used to be recentred on their bounding box as well, which was wrong: an
 * edge component's origin is the point it bolts onto the hull, and its barrel
 * or bell reaches out well past that, so "centre" is nowhere near where the
 * part actually attaches. Recentring shifted every one of them off its mount.
 * The authored origin is now taken as the truth, exactly as the bounds in
 * `footprints.generated.ts` are measured about it.
 */

import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';

import { withEdges } from './shaders/structure';

/**
 * Served from `apps/client/public/models`, under the app's base path.
 *
 * Guarded because `import.meta.env` is Vite's and does not exist under plain
 * node — so importing anything that reaches this module from a test threw at
 * load, before a single assertion ran. Nothing under test fetches a model; they
 * only need the module to load.
 */
const MODEL_BASE = `${import.meta.env?.BASE_URL ?? '/'}models/`;

const cache = new Map<string, Promise<THREE.BufferGeometry | null>>();

/**
 * Geometries handed out by the cache.
 *
 * Scenes dispose what they built when they tear down, and a shared geometry
 * must survive that — freeing it would leave the next scene's components
 * pointing at a released buffer.
 */
const shared = new Set<THREE.BufferGeometry>();

/** Is this geometry owned by the cache, and therefore not the caller's to free? */
export function isSharedGeometry(geometry: THREE.BufferGeometry): boolean {
  return shared.has(geometry);
}

/**
 * One geometry per model, shared by every instance and never disposed.
 *
 * Components are placed and removed constantly in the editor; re-parsing and
 * re-deriving the crease mask on every placement would be pure waste.
 */
export function loadComponentGeometry(name: string): Promise<THREE.BufferGeometry | null> {
  const existing = cache.get(name);
  if (existing) return existing;

  const loading = new OBJLoader()
    .loadAsync(`${MODEL_BASE}${name}.obj`)
    .then((group) => {
      const meshes: THREE.BufferGeometry[] = [];
      group.traverse((object) => {
        const mesh = object as THREE.Mesh;
        if (mesh.isMesh && mesh.geometry) meshes.push(mesh.geometry);
      });

      const geometry = meshes[0];
      if (!geometry) return null;

      // Y-up (footprint in XZ) to Z-up (footprint in XY). Nothing else: the
      // model's own origin is where the part goes.
      geometry.rotateX(Math.PI / 2);

      const tagged = withEdges(geometry);
      shared.add(tagged);
      return tagged;
    })
    .catch(() => null);

  cache.set(name, loading);
  return loading;
}
